import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Lock, ShieldCheck, Phone, Mail, MapPin, MessageSquare, FileText, KeyRound, Building2, X, Star } from "lucide-react";

/**
 * BuyerProtectionPanel — locked seller-details panel with paid unlock flow.
 *
 * Renders one of three states:
 *   • locked  → premium "Unlock Seller Details" panel with fee + benefits
 *   • unlocked → seller contact grid + advisory transaction guidance panel
 *   • seller-view/admin → no-op (returns null; VehicleDetail shows contacts natively)
 */
export const BuyerProtectionPanel = ({ vehicle, onUnlocked }) => {
  const { user } = useAuth();
  const [fee, setFee] = useState(null);
  const [status, setStatus] = useState({ unlocked: false, unlock: null });
  const [busy, setBusy] = useState(false);
  const [advisoryOpen, setAdvisoryOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [subBusy, setSubBusy] = useState(null);

  const isOwner = user && user.user_id === vehicle.seller_id;
  const isAdmin = user && user.role === "admin";
  const contactLocked = vehicle?.seller?.contact_locked;

  useEffect(() => {
    if (!vehicle?.id || isOwner || isAdmin) return;
    api.get("/buyer-protection/fee").then(r => setFee(r.data)).catch(() => {});
    api.get("/buyer-protection/subscription/plans").then(r => setPlans(r.data)).catch(() => {});
    if (user) {
      api.get(`/buyer-protection/${vehicle.id}/status`).then(r => setStatus(r.data)).catch(() => {});
      api.get("/buyer-protection/subscription/mine").then(r => setSubscription(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [vehicle?.id, user?.user_id]);

  if (isOwner || isAdmin || !contactLocked || !vehicle?.buyer_protection) return null;

  const unlock = async () => {
    if (!user) { toast.info("Please sign in to unlock seller details."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/buyer-protection/unlock/${vehicle.id}`, {});
      if (r.data.already_unlocked || r.data.unlock) {
        toast.success("Seller details unlocked.");
        setStatus({ unlocked: true, unlock: r.data.unlock });
        setAdvisoryOpen(true);
        onUnlocked?.();
      } else {
        toast.error("Payment did not complete. Please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlock failed");
    } finally { setBusy(false); }
  };

  const subscribe = async (plan_key) => {
    if (!user) { toast.info("Please sign in to subscribe."); return; }
    setSubBusy(plan_key);
    try {
      const r = await api.post("/buyer-protection/subscription/purchase", { plan_key });
      if (r.data.subscription) {
        toast.success(`${r.data.plan.label} active — unlimited unlocks for 30 days.`);
        setSubscription({ ...r.data.subscription, plan: r.data.plan });
        setAdvisoryOpen(true);
        onUnlocked?.();
      } else {
        toast.error("Payment did not complete.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription failed");
    } finally { setSubBusy(null); }
  };

  return (
    <>
      <div
        className="mt-4 border border-primary/30 bg-gradient-to-br from-primary/[0.04] via-background to-background rounded-sm overflow-hidden"
        data-testid="buyer-protection-panel"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-sm bg-primary/10 grid place-items-center shrink-0">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="kicker">Buyer Verification Panel</div>
              <div className="display text-2xl leading-tight mt-1">Verified Seller</div>
              <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                This seller has completed THOM Verify™ verification. To protect both buyer and seller, personal contact details are released only after Buyer Verification Access has been purchased.
              </p>
            </div>
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            {[
              "Verified seller identity",
              "Verified phone number",
              "Verified email",
              "Verified physical address (where available)",
              "THOM Verify Trust Score",
              "Inspection history",
              "Vehicle ownership verification (where available)",
              "Vehicle status checks",
              "Certificate verification",
              "Secure communication record",
            ].map(b => (
              <div key={b} className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>{b}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="kicker">Buyer Verification Access</div>
              <div className="display text-3xl mt-0.5">
                {fee ? `$${Number(fee.amount_usd).toFixed(2)} ${fee.currency}` : "—"}
              </div>
              <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mt-1">
                One-off · 30-day access · Auditable
              </div>
            </div>
            <Button
              size="lg"
              onClick={unlock}
              disabled={busy}
              className="min-w-[220px]"
              data-testid="btn-unlock-seller"
            >
              {busy ? "Processing…" : (
                <span className="inline-flex items-center gap-2"><KeyRound className="h-4 w-4" /> Unlock Seller Details</span>
              )}
            </Button>
          </div>

          {plans.length > 0 && (
            <div className="mt-6 pt-6 border-t border-border" data-testid="unlock-pass-plans">
              <div className="flex items-center gap-2 mb-3">
                <Star className="h-4 w-4 text-primary" />
                <div className="kicker">Or subscribe once — unlock every verified listing</div>
              </div>
              <div className="text-xs text-muted-foreground mb-4 max-w-2xl">
                Designed for banks, insurers, fleet operators, dealers, and corporate procurement teams who
                review many listings per month. All-you-can-view seller-contact access across every THOM Verified™
                vehicle — one subscription, monthly billing.
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {plans.filter(p => p.audience || p.key.startsWith("unlock_pass_")).map(p => {
                  const active = subscription && subscription.plan_key === p.key;
                  return (
                    <div key={p.key} className={`border rounded-sm p-4 flex flex-col gap-3 ${active ? "border-primary bg-primary/5" : "border-border bg-background"}`} data-testid={`plan-${p.key}`}>
                      <div>
                        <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-widest text-muted-foreground">
                          {p.audience === "enterprise_buyer" ? "Enterprise" : "Professional"}
                          {active && <span className="text-primary">· Active</span>}
                        </div>
                        <div className="display text-xl mt-0.5">{p.label}</div>
                        <div className="mt-2 flex items-baseline gap-1">
                          <span className="display text-3xl">${p.price_usd_month}</span>
                          <span className="text-xs text-muted-foreground">/ month</span>
                        </div>
                      </div>
                      <ul className="text-xs space-y-1 text-muted-foreground flex-1">
                        <li className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-primary" /> Unlimited unlocks on every verified listing</li>
                        {p.entitlements?.team_seats && <li className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-primary" /> {p.entitlements.team_seats} team seats</li>}
                        {p.entitlements?.dedicated_account_manager && <li className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-primary" /> Dedicated account manager</li>}
                        {p.entitlements?.api_access && <li className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-primary" /> API access</li>}
                      </ul>
                      {active ? (
                        <div className="text-[10px] mono uppercase tracking-widest text-primary">
                          Active until {new Date(subscription.expires_at).toLocaleDateString()}
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          onClick={() => subscribe(p.key)}
                          disabled={subBusy === p.key}
                          data-testid={`btn-subscribe-${p.key}`}
                        >
                          {subBusy === p.key ? "Processing…" : "Start subscription"}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!user && (
            <div className="mt-4 text-xs text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">Sign in</Link> to unlock this listing.
            </div>
          )}
        </div>
      </div>

      {/* Advisory dialog shown once unlock succeeds */}
      {advisoryOpen && status.unlocked && (
        <AdvisoryDialog onClose={() => setAdvisoryOpen(false)} />
      )}
    </>
  );
};


export const UnlockedSellerCard = ({ vehicle }) => {
  const s = vehicle?.seller || {};
  if (s.contact_locked) return null;
  return (
    <div className="mt-4 border border-border bg-card p-5" data-testid="unlocked-seller-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <div className="kicker">Verified Seller Contact</div>
        <span className="ml-auto text-[10px] mono uppercase tracking-widest text-primary border border-primary/40 px-2 py-0.5 rounded-sm">Unlocked</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        {s.name && <Row Icon={Building2} label="Seller Name" value={s.name} />}
        {s.email && <Row Icon={Mail} label="Email" value={<a href={`mailto:${s.email}`} className="hover:text-primary">{s.email}</a>} />}
        {s.phone && <Row Icon={Phone} label="Telephone" value={<a href={`tel:${s.phone}`} className="hover:text-primary">{s.phone}</a>} />}
        {s.address && <Row Icon={MapPin} label="Location" value={s.address} />}
      </div>
      <div className="mt-4 pt-3 border-t border-border/50 flex items-center gap-2 text-xs text-muted-foreground">
        <MessageSquare className="h-3.5 w-3.5" />
        Every interaction with this seller is logged into your Digital Passport transaction record.
      </div>
    </div>
  );
};


const AdvisoryDialog = ({ onClose }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="tx-advisory-modal">
    <div className="max-w-lg w-full bg-card border border-border rounded-sm shadow-2xl">
      <div className="p-5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <div className="display text-lg">Recommended Transaction Process</div>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>
      <div className="p-6 text-sm leading-relaxed space-y-4">
        <p>
          THOM Verify™ recommends completing your transaction through our secure platform to benefit from available verification services, inspection records, payment support (where offered), and transaction documentation.
        </p>
        <p>
          Using THOM Verify helps create an <b>auditable transaction history</b> and may provide access to additional platform-supported services.
        </p>
        <p className="text-muted-foreground">
          Transactions completed outside THOM Verify are not covered by THOM Verify platform services, verification workflows, or transaction records.
        </p>
      </div>
      <div className="p-4 border-t border-border flex justify-end">
        <Button onClick={onClose} data-testid="btn-advisory-understand">I Understand</Button>
      </div>
    </div>
  </div>
);


const Row = ({ Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="h-3.5 w-3.5 text-muted-foreground mt-1" />
    <div>
      <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  </div>
);


export default BuyerProtectionPanel;
